import type { AxiosProgressEvent } from 'axios';
import { getBaseUrl, request } from 'src/helper/api';
import type { StateStatusActions } from './types';

/**
 * Gets all service requests.
 * @param {string} [token] - Optional authentication token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 * @returns {Promise<AxiosResponse<ServiceRequestAPIResponseObj[]>>} - A promise that
 *  resolves to the response of the request.
 */
export const getServiceRequests = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests`,
      contentType: 'json',
      token,
      signal,
    },
    'service-request/getServiceRequests',
  );

/**
 * Retrieves the details of a specific service request.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [ServiceRequestId] - The ID of the service request to retrieve details for.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 * @returns {Promise<AxiosResponse<ServiceRequestAPIResponseObj>>} - A promise that
 *  resolves to the response of the request.
 */
export const getServiceRequestDetails = (
  token?: string,
  ServiceRequestId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'GET',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${ServiceRequestId}`,
      contentType: 'json',
      token,
      signal,
    },
    'service-request/getServiceRequestDetails',
  );

/**
 * Adds an assignee to a specific service request.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [serviceRequestId] - The ID of the service request
 *  to which the assignee will be added.
 * @param {string} [userId] - The ID of the user to be assigned to the service request.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const addSrAssignee = (
  token?: string,
  serviceRequestId?: string,
  userId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${serviceRequestId}/assignee/${userId}/add`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'service-request/addSrAssignee',
  );

/**
 * Removes an assignee from a specific service request.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [serviceRequestId] - The ID of the service request
 *  from which the assignee will be removed.
 * @param {string} [userId] - The ID of the user to be removed
 *  as an assignee from the service request.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const removeSrAssignee = (
  token?: string,
  serviceRequestId?: string,
  userId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'DELETE',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${serviceRequestId}/assignee/${userId}`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'service-request/removeSrAssignee',
  );

/**
 * Updates a service request with the given details.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [ServiceRequestId] - The ID of the service request
 *  to be updated.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const updateServiceRequestDetails = (
  token?: string,
  ServiceRequestId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'PUT',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${ServiceRequestId}`,
      contentType: 'json',
      token,
      signal,
    },
    'service-request/updateServiceRequestDetails',
  );

/**
 * PATCH service request access method
 * @param {string} [token] - Optional authentication token.
 * @param {string} [ServiceRequestId] - The ID of the service request to update.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const updateServiceRequestAccessMethod = (token?: string, ServiceRequestId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${ServiceRequestId}/access-method`,
      contentType: 'json',
      token,
    },
    'service-requests/updateServiceRequestAccessMethod',
    { ServiceRequestId },
  );

/**
 * PATCH service request description
 * @param {string} [token] - Optional authentication token.
 * @param {string} [ServiceRequestId] - The ID of the service request to update.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const updateServiceRequestDescription = (token?: string, ServiceRequestId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${ServiceRequestId}/description`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'service-requests/updateServiceRequestDescription',
  );

/**
 * PUT service request resident info
 * @param {string} [token] - Authentication token
 * @param {string} [ServiceRequestId] - Service request ID
 * @param {AbortSignal} [signal] - Abort signal to cancel the request
 * @returns {Promise<AxiosResponse<ServiceRequestAPIResponseObj>>}
 */
export const updateServiceRequestResidentInfo = (
  token?: string,
  ServiceRequestId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'PUT',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${ServiceRequestId}/actions/UpdateResident`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'service-request/updateServiceRequestResidentInfo',
  );

/**
 * Uploads a media thumbnail for a specific service request.
 * @param {string} ServiceRequestId - The ID of the service request
 * for which the thumbnail will be uploaded.
 * @param {string} [token] - Optional authentication token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const uploadSrMediaThumbnail = (
  ServiceRequestId: string,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/service-requests/${ServiceRequestId}/media/thumbnail`,
      contentType: 'form-data',
      token,
      signal,
    },
    'service-request/uploadSrMediaThumbnail',
  );
export const uploadSrMedia = (
  ServiceRequestId: string,
  token?: string,
  signal?: AbortSignal,
  onUploadProgress?: (pct: number) => void,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/service-requests/${ServiceRequestId}/media/upload`,
      contentType: 'form-data',
      token,
      signal,
      onUploadProgress: onUploadProgress
        ? (e: AxiosProgressEvent) => {
            if (e.total) onUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        : undefined,
    },
    'service-request/uploadSrMedia',
  );
/**
 * Uploads a media file for a specific service request.
 * @param {string} srId - The ID of the service request for which the media will be uploaded.
 * @param {string} mediaId - The ID of the media to be uploaded.
 * @param {string} [token] - Optional authentication token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const uploadServiceRequestMedia = (
  srId: string,
  mediaId: string,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/service-requests/${srId}/media/${mediaId}/upload`,
      contentType: 'form-data',
      token,
      signal,
    },
    'service-request/uploadServiceRequestMedia',
  );
/**
 * Deletes a media file associated with a service request.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [service_order_uuid] - The ID of the service request
 * from which the media will be deleted.
 * @param {string} [media_uuid] - The ID of the media to be deleted.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const deleteServiceRequestMedia = (
  token?: string,
  service_order_uuid?: string,
  media_uuid?: string,
) =>
  request(
    {
      method: 'DELETE',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${service_order_uuid}/media/${media_uuid}`,
      contentType: 'json',
      token,
    },
    'service-request/deleteServiceRequestMedia',
  );
// Update actions for service request status
/**
 * Updates the status of a service request.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [serviceRequestId] - The ID of the service request to update.
 * @param {StateStatusActions | string} [stateAction] - The state action to perform.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const updateServiceRequestStatus = (
  token?: string,
  serviceRequestId?: string,
  stateAction?: StateStatusActions | string,
) =>
  request(
    {
      method: 'POST',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${serviceRequestId}/actions/${stateAction}`,
      contentType: 'json',
      token,
    },
    'service-request/updateServiceRequestStatus',
  );

/**
 * Updates the resident availability for a specific service request.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [serviceRequestId] - The ID of the service request to update.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const updateResidentAvailability = (token?: string, serviceRequestId?: string) =>
  request(
    {
      method: 'PUT',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${serviceRequestId}/actions/updateResidentAvailability`,
      contentType: 'json',
      token,
    },
    'service-request/updateResidentAvailability',
  );

/**
 * Adds resident availability for a specific service request.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [serviceRequestId] - The ID of the service request
 * to which the resident availability will be added.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const addResidentAvailability = (token?: string, serviceRequestId?: string) =>
  request(
    {
      method: 'POST',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${serviceRequestId}/actions/addResidentAvailability`,
      contentType: 'json',
      token,
    },
    'service-request/addResidentAvailability',
  );

/**
 * Requests resident availability for a specific service request.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [serviceRequestId] -
 *  The ID of the service request for which the resident availability is requested.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const requestResidentAvailability = (token?: string, serviceRequestId?: string) =>
  request(
    {
      method: 'POST',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${serviceRequestId}/actions/requestResidentAvailability`,
      contentType: 'json',
      token,
    },
    'service-request/requestResidentAvailability',
  );
export const availabilityViewed = (token?: string, serviceRequestId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/service-requests/${serviceRequestId}/availability-viewed`,
      contentType: 'json',
      token,
    },
    'service-request/availabilityViewed',
  );
/**
 * Creates a new work order for the given service request.
 * @param {string} ServiceRequestId - The ID of the service request
 * for which the work order will be created.
 * @param {string} [token] - Optional authentication token.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const createWorkOrder = (ServiceRequestId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${ServiceRequestId}/actions/workOrder`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'service-request/createWorkOrder',
  );

/**
 * Undo canceling an issue for the given service request.
 * @param {string} [serviceRequestId] - The ID of the service request
 * for which the issue will be un-canceled.
 * @param {string} [issueId] - The ID of the issue to be un-canceled.
 * @param {string} [token] - Optional authentication token.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const undoCanceledIssue = (serviceRequestId?: string, issueId?: string, token?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/service-requests/${serviceRequestId}/issues/${issueId}/actions/restoreIssue`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'service-request/undoCanceledIssue',
  );

/**
 * Undo deleting an issue for the given service request.
 * @param {string} [serviceRequestId] - The ID of the service request
 * for which the issue will be un-deleted.
 * @param {string} [issueId] - The ID of the issue to be un-deleted.
 * @param {string} [token] - Optional authentication token.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const undoIssue = (serviceRequestId?: string, issueId?: string, token?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/service-requests/${serviceRequestId}/issues/${issueId}/actions/undoIssue`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'service-request/undoIssue',
  );

export const updateServiceRequestPropertyAddress = (serviceRequestId?: string, token?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/service-requests/${serviceRequestId}/actions/updatePropertyAddress`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'service-request/updateServiceRequestPropertyAddress',
  );

export const updateServiceRequestPriority = (serviceRequestId?: string, token?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/service-requests/${serviceRequestId}/actions/updatePriority`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'service-request/updateServiceRequestPriority',
  );

export const getSRCreateAddressList = (token?: string, cursor?: string | null) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/service-requests/property-address-list${cursor ? `?cursor=${cursor}` : ''}`,
      contentType: 'json',
      token,
    },
    'service-request/getSRCreateAddressList',
  );

export const createServiceRequest = (token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/service-requests/actions/createServiceRequest`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'service-request/createServiceRequest',
  );

export const uploadIssueMedia = (
  ServiceRequestId: string,
  token?: string,
  signal?: AbortSignal,
  onUploadProgress?: (pct: number) => void,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/service-requests/${ServiceRequestId}/issues/media/upload`,
      contentType: 'form-data',
      token,
      signal,
      onUploadProgress: onUploadProgress
        ? (e: AxiosProgressEvent) => {
            if (e.total) onUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        : undefined,
    },
    'service-request/uploadIssueMedia',
  );

export const deleteIssueMedia = (
  token?: string,
  service_order_uuid?: string,
  media_uuid?: string,
) =>
  request(
    {
      method: 'DELETE',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${service_order_uuid}/issues/media/${media_uuid}`,
      contentType: 'json',
      token,
    },
    'service-request/deleteIssueMedia',
  );

/**
 * Rejects a service request with an internal reason and optional resident notification.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [serviceRequestId] - The ID of the service request to reject.
 * @returns {Promise<AxiosResponse>} - A promise that resolves to the response of the request.
 */
export const rejectServiceRequest = (token?: string, serviceRequestId?: string) =>
  request(
    {
      method: 'POST',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${serviceRequestId}/actions/rejectServiceRequest`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'service-request/rejectServiceRequest',
    { serviceRequestId },
  );

export const bulkRequestResidentAvailability = (token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/service-requests/actions/bulkRequestResidentAvailability`,
      contentType: 'json',
      token,
    },
    'service-request/bulkRequestResidentAvailability',
  );

export const getServiceRequestsForGroup = (
  token?: string,
  groupKey?: string,
  signal?: AbortSignal,
  attemptId?: number,
) =>
  request(
    {
      method: 'GET',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests`,
      contentType: 'json',
      token,
      signal,
    },
    'service-request/getServiceRequestsForGroup',
    { _group_key: groupKey, _attempt: attemptId },
  );
